import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { authApi } from '@/features/auth/api';
import { brandsApi } from '@/features/brands/api';
import { useAuth } from '@/lib/auth/useAuth';
import { useAuthStore } from '@/lib/auth/authStore';
import {
  useBrandContextStore,
  useSelectedBrandId,
} from '@/lib/branding/brandContextStore';
import { queryKeys } from '@/lib/api/queryKeys';

/**
 * After ProtectedRoute, this gate enforces the onboarding tree:
 *   - role staff/viewer  → /pending-access
 *   - no companyId       → /onboarding/brand-profile
 *   - !hasBrandKit       → /onboarding/brandkit
 *   - non-full user, no selectedBrandId → auto-pick first accessible brand
 *   - non-full user on `/`                → redirect to `/brand/:selectedBrandId`
 *   - else                              → render
 *
 * The login response does NOT include `hasBrandKit` (the backend infers it
 * from `co_brandkits`). So this gate also drives a one-shot query against
 * `GET /auth/onboarding-status` and patches the auth store with the result.
 * Without this, every freshly logged-in user would be bounced to /onboarding
 * even if their company already has a brand kit configured.
 */
export function OnboardingGate() {
  const { user, role } = useAuth();
  const patchUser = useAuthStore((s) => s.patchUser);
  const app = useAuthStore((s) => s.app);
  const location = useLocation();
  const selectedBrandId = useSelectedBrandId();
  const setSelectedBrandId = useBrandContextStore((s) => s.setSelectedBrandId);

  const statusQ = useQuery({
    queryKey: queryKeys.auth.onboarding,
    queryFn: () => authApi.onboardingStatus(),
    enabled: !!user && user.hasBrandKit === undefined,
    staleTime: 60_000,
    retry: false,
  });

  // Brands accessible to the logged-in user. Used to auto-select the first
  // one for non-full users so they always have a working brand context.
  const brandsQ = useQuery({
    queryKey: queryKeys.brands.all,
    queryFn: () => brandsApi.list(),
    enabled: !!user?.hasBrandKit && !!user?.companyId,
    staleTime: 60_000,
  });
  const brands = brandsQ.data?.items ?? [];

  useEffect(() => {
    if (statusQ.data && user?.hasBrandKit === undefined) {
      patchUser({ hasBrandKit: statusQ.data.hasBrandKit });
    }
  }, [statusQ.data, user?.hasBrandKit, patchUser]);

  // Repair stale or missing brand selection. If the persisted id no longer
  // matches any accessible brand (revoked access, deleted brand), drop it
  // and pick the first available so non-full users never see a blank brand.
  useEffect(() => {
    if (brands.length === 0) return;
    const stillValid =
      selectedBrandId != null && brands.some((b) => b.id === selectedBrandId);
    if (!stillValid) {
      setSelectedBrandId(brands[0].id);
    }
  }, [brands, selectedBrandId, setSelectedBrandId]);

  // Caso 11 — operator sessions. The token exchange
  // (`POST /xk-station/auth/exchange`) issues a JWT with `app: 'operator'`,
  // and OperatorLoginPage stamps a synthetic `role: 'staff'` on the session
  // (the real restriction is the server-side `app` claim, not a FE role).
  // That synthetic role would otherwise trip the staff/viewer branch below
  // and bounce the operator to /pending-access. Operator sessions are
  // legitimate — let them straight through the whole onboarding tree so the
  // station view and its redeem/QR shortcuts render normally.
  if (app === 'operator') {
    return <Outlet />;
  }

  // Defer redirect decision while we don't yet know hasBrandKit — otherwise
  // we'd bounce the user to /onboarding only to immediately bounce back once
  // the status query resolves.
  if (user && user.hasBrandKit === undefined && statusQ.isLoading) {
    return null;
  }

  if (role === 'staff' || role === 'viewer') {
    if (location.pathname !== '/pending-access') {
      return <Navigate to="/pending-access" replace />;
    }
    return <Outlet />;
  }

  if (!user?.companyId && !location.pathname.startsWith('/onboarding/')) {
    return <Navigate to="/onboarding/brand-profile" replace />;
  }

  if (
    user?.companyId &&
    !user.hasBrandKit &&
    !location.pathname.startsWith('/onboarding/brandkit')
  ) {
    return <Navigate to="/onboarding/brandkit" replace />;
  }

  if (
    user?.hasBrandKit &&
    location.pathname.startsWith('/onboarding/')
  ) {
    return <Navigate to="/" replace />;
  }

  // Xamplin (build-v2 DU-1): Home `/` is the landing for every role — render
  // the index (HomePage command center) instead of bouncing to a brand page.
  return <Outlet />;
}
